// @ts-nocheck
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Copy, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

type Role = 'rlevents_super_admin' | 'rlevents_tenant_admin' | 'rlevents_tenant_staff';

const schema = z.object({
  role: z.enum(['rlevents_super_admin', 'rlevents_tenant_admin', 'rlevents_tenant_staff']),
  organization_id: z.string().optional(),
  invitee_email: z.string().email('Valid email required'),
  invitee_name: z.string().optional(),
  invitee_phone: z.string().optional(),
  personal_message: z.string().max(500).optional(),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  callerIsSuperAdmin: boolean;
  defaultOrganizationId?: string;
  onInvited?: () => void;
}

export function InviteAdminDialog({
  open,
  onOpenChange,
  callerIsSuperAdmin,
  defaultOrganizationId,
  onInvited,
}: Props) {
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      role: callerIsSuperAdmin ? ('rlevents_tenant_admin' as Role) : ('rlevents_tenant_staff' as Role),
      organization_id: defaultOrganizationId,
      invitee_email: '',
      invitee_name: '',
      invitee_phone: '',
      personal_message: '',
    },
  });

  const role = form.watch('role');

  useEffect(() => {
    if (!open) return;
    setError(null);
    setMagicLink(null);
    setEmailError(null);
    (async () => {
      let q = (supabase as any).from('organizations').select('id, name').eq('is_active', true).order('name');
      const { data } = await q;
      let list = data ?? [];
      if (!callerIsSuperAdmin && defaultOrganizationId) {
        list = list.filter((o: any) => o.id === defaultOrganizationId);
      }
      setOrgs(list);
    })();
  }, [open, callerIsSuperAdmin, defaultOrganizationId]);

  const onSubmit = async (values: FormValues) => {
    setError(null);
    setMagicLink(null);
    setEmailError(null);

    if (values.role !== 'rlevents_super_admin' && !values.organization_id) {
      setError('Organization is required for tenant roles');
      return;
    }

    const { data, error: fnErr } = await (supabase as any).functions.invoke('rle-invite-admin', {
      body: {
        invitee_email: values.invitee_email,
        invitee_name: values.invitee_name || null,
        invitee_phone: values.invitee_phone || null,
        role: values.role,
        organization_id: values.role === 'rlevents_super_admin' ? null : values.organization_id,
        personal_message: values.personal_message || null,
      },
    });

    if (fnErr) {
      setError(fnErr.message ?? 'Failed to send invitation');
      return;
    }
    if (!data?.ok) {
      const code = data?.error?.code;
      if (code === 'ALREADY_INVITED') {
        setError('An invitation is already pending for this email. Revoke the existing invite first.');
      } else if (code === 'UNAUTHORIZED_ROLE') {
        setError("You don't have permission to assign this role.");
      } else if (code === 'INVALID_EMAIL') {
        setEmailError('Invalid email address');
      } else {
        setError(data?.error?.message ?? 'Failed to send invitation');
      }
      return;
    }

    if (data.data?.email_sent) {
      toast.success(`Invitation sent to ${values.invitee_email}`);
      onInvited?.();
      onOpenChange(false);
      form.reset();
    } else {
      // Show manual fallback
      setMagicLink(data.data?.magic_link ?? null);
      setEmailError(data.data?.email_error ?? null);
      toast.warning('Invite created but email failed to send');
      onInvited?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite admin</DialogTitle>
          <DialogDescription>
            Send an invitation email. The recipient will set up their account via the link.
          </DialogDescription>
        </DialogHeader>

        {magicLink ? (
          <div className="space-y-3">
            <Alert>
              <AlertDescription>
                Invitation created but email delivery failed. Share this link manually:
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Input value={magicLink} readOnly className="text-xs" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(magicLink);
                  toast.success('Link copied');
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {emailError && <p className="text-xs text-muted-foreground">Email error: {emailError}</p>}
            <Button
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                form.reset();
              }}
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>Role</Label>
              <Select
                value={form.watch('role')}
                onValueChange={(v) => form.setValue('role', v as Role)}
                disabled={!callerIsSuperAdmin}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {callerIsSuperAdmin && (
                    <>
                      <SelectItem value="rlevents_super_admin">Super Admin — Full platform access</SelectItem>
                      <SelectItem value="rlevents_tenant_admin">Organization Admin — Manages one organization</SelectItem>
                    </>
                  )}
                  <SelectItem value="rlevents_tenant_staff">Staff — Limited org permissions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {role !== 'rlevents_super_admin' && (
              <div>
                <Label>Organization</Label>
                <Select
                  value={form.watch('organization_id') ?? ''}
                  onValueChange={(v) => form.setValue('organization_id', v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Email *</Label>
              <Input type="email" {...form.register('invitee_email')} className={emailError ? 'border-destructive' : ''} />
              {(form.formState.errors.invitee_email || emailError) && (
                <p className="text-xs text-destructive mt-1">
                  {emailError || form.formState.errors.invitee_email?.message}
                </p>
              )}
            </div>
            <div>
              <Label>Name</Label>
              <Input {...form.register('invitee_name')} placeholder="Optional" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input {...form.register('invitee_phone')} placeholder="Optional" />
            </div>
            <div>
              <Label>Personal message</Label>
              <Textarea {...form.register('personal_message')} placeholder="Optional" rows={3} maxLength={500} />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send invitation
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InviteAdminDialog;
